import { useEffect, useState } from 'react';

import { FAQ_ITEMS } from '@/components/FAQ/FAQModel';
import { ancora, estruturarResposta } from '@/components/v2/faq/faqGrupos';
import Reveal from '@/components/v2/primitives/Reveal';
import { LINK_COMUNIDADE_WHATSAPP } from '@/constants/links';
import { HUE_MARCA } from '@/design/hues';

/**
 * As dúvidas, como banda da home.
 *
 * Lista única, sem os três temas que a página tinha: para onze
 * perguntas o agrupamento só atrapalha, e a navegação por tema não
 * cabe numa seção curta.
 *
 * Uma pergunta aberta por vez. Com todas abertas a seção empurraria o
 * rodapé para longe e a leitora perderia o lugar.
 *
 * As âncoras por pergunta continuam valendo: /faq#quanto-custa-participar
 * já era link válido e virou /#quanto-custa-participar, com o mesmo id
 * gerado pela mesma função.
 */
export const Duvidas = () => {
  const [aberta, setAberta] = useState(null);

  /* Chegando por âncora de pergunta, ela abre. Sem isto o link levaria
     à seção com tudo fechado, que é o mesmo que não levar a lugar
     nenhum.
     Quem rola é o ScrollToTop, e só ele: aqui a abertura muda a altura
     da seção, e dois componentes disputando a posição deixariam a
     página tremendo. */
  useEffect(() => {
    const alvo = window.location.hash.replace('#', '');
    if (!alvo) return;

    const item = FAQ_ITEMS.find((q) => ancora(q.pergunta) === alvo);
    if (item) setAberta(item.id);
  }, []);

  return (
    <section id="duvidas" data-hue={HUE_MARCA} className="banda-creme">
      <div className="mx-auto w-full max-w-[1100px] px-5 py-20 md:px-6 md:py-32">
        <Reveal>
          <h2 className="type-club mb-14 text-center text-[clamp(31px,5.8vw,66px)] text-[var(--banda-titulo)]">
            Você tem alguma dessas dúvidas?
          </h2>
        </Reveal>

        <div className="mx-auto grid max-w-[720px] gap-3.5">
          {FAQ_ITEMS.map((item) => {
            const id = ancora(item.pergunta);
            const estaAberta = aberta === item.id;

            return (
              <div
                key={item.id}
                id={id}
                className="overflow-hidden rounded-[10px] border border-[var(--banda-botao)] bg-white scroll-mt-24"
              >
                <button
                  type="button"
                  aria-expanded={estaAberta}
                  aria-controls={`resposta-${item.id}`}
                  onClick={() => setAberta(estaAberta ? null : item.id)}
                  className="flex w-full items-center justify-between gap-5 px-6 py-5 text-left text-[clamp(14px,1.6vw,16px)] font-semibold leading-snug text-[var(--banda-acento)] transition-colors duration-200 hover:bg-[color-mix(in_srgb,var(--color-rosa-banda)_12%,transparent)]"
                >
                  {item.pergunta}
                  <span
                    aria-hidden="true"
                    className={`shrink-0 text-[24px] font-normal leading-none text-[var(--banda-botao)] transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                      estaAberta ? 'rotate-45' : ''
                    }`}
                  >
                    +
                  </span>
                </button>

                {estaAberta && (
                  <div
                    id={`resposta-${item.id}`}
                    className="px-6 pb-5 text-[15px] leading-relaxed text-[#5f4049]"
                  >
                    {estruturarResposta(item.resposta).map((bloco, i) =>
                      bloco.tipo === 'lista' ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className="mb-2.5 last:mb-0">
                          {/* A introdução é a frase que apresenta a lista.
                              Descartá-la deixava a resposta começando no
                              meio. */}
                          {bloco.introducao && (
                            <p className="mb-1.5">{bloco.introducao}</p>
                          )}
                          <ul className="grid list-disc gap-1.5 pl-5">
                            {bloco.itens.map((li) => (
                              <li key={li}>{li}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        // eslint-disable-next-line react/no-array-index-key
                        <p key={i} className="mb-2.5 last:mb-0">
                          {bloco.texto}
                        </p>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Reveal className="mx-auto mt-12 flex max-w-[720px] flex-wrap items-center justify-between gap-6 rounded-[12px] bg-[color-mix(in_srgb,var(--color-rosa-banda)_42%,white)] px-7 py-7">
          <p className="type-club m-0 max-w-[22ch] text-[clamp(20px,2.8vw,30px)] text-[#3d1220]">
            Essas respostas não respondem à sua dúvida?
          </p>
          <a
            href={LINK_COMUNIDADE_WHATSAPP}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-full bg-[var(--banda-botao)] px-8 py-4 text-[12.5px] font-bold uppercase tracking-[0.12em] text-white no-underline transition-transform duration-300 hover:-translate-y-0.5"
          >
            Quero tirar uma dúvida
          </a>
        </Reveal>
      </div>
    </section>
  );
};

export default Duvidas;
